import dayjs from 'dayjs';
import { formatCurrency, numberToThaiText } from '../utils/helpers';
import { loadImageBase64 } from "../utils/loadImageBase64";
import { createDocDefinitionFullStyle } from './DocumentStyles/ReceiptDefinitionFull';
import { createDocDefinitionDotStyle } from './DocumentStyles/ReceiptDefinitionDot';
import { createDocDefinitionA4Reference } from './DocumentStyles/ReceiptDefinitionA4Reference';
import { createDeliveryInvoiceDefinition } from './DocumentStyles/DeliveryInvoiceDefinition';
import { createDepositReceiptDefinition } from './DocumentStyles/DepositReceiptDefinition';
import { buildUploadUrl } from '../config/runtimeConfig';
import { getCompanySignatureLayout, normalizeSignatureLayout } from '../utils/signatureLayout';
import { getContactDocumentName } from '../utils/contactDisplayName';
import { buildReceiptItemDescription, buildReceiptItemQuantity } from './DocumentStyles/receiptItemDisplay';

// กำหนด font สำหรับ pdfMake
const initializePdfMake = () => {
    if (window.pdfMake) {
        window.pdfMake.fonts = {
            THSarabunNew: {
                normal: 'THSarabunNew.ttf',
                bold: 'THSarabunNew Bold.ttf',
                italics: 'THSarabunNew Italic.ttf',
                bolditalics: 'THSarabunNew BoldItalic.ttf'
            }
        };
    }
};

const buildReceiptPdfFileName = (receipt = {}) => {
    const documentNumber = (
        receipt.status === 'paid'
            ? receipt.receipt_number
            : receipt.invoice_number
    ) || receipt.receipt_number || receipt.invoice_number || 'receipt';

    const safeDocumentNumber = documentNumber
        .toString()
        .trim()
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_');

    return `${safeDocumentNumber || 'receipt'}.pdf`;
};

export const generateReceiptPDF = async (
    receipt,
    company = {},
    printType = 'full',
    signatureLayout = null,
    printOptions = {}
) => {

    //กรณีใช้ local logo:   process.env.PUBLIC_URL + "/assets/ttlogo.png"
    const logoBase64 = await loadImageBase64(buildUploadUrl(company?.logo_path));
    const showSignature = printOptions.show_signature !== false;
    const showStamp = printOptions.show_stamp !== false;
    const SignatureLogo = showSignature
        ? await loadImageBase64(buildUploadUrl(company?.signature_path))
        : null;
    const StampLogo = showStamp
        ? await loadImageBase64(buildUploadUrl(company?.stamp_path), { transparentWhite: true })
        : null;

    // ตรวจว่ามี prefix หรือไม่
    let fixedSignatureLogo = SignatureLogo;
    if (SignatureLogo && !SignatureLogo.startsWith('data:image')) {
        fixedSignatureLogo = `data:image/png;base64,${SignatureLogo}`;
    }
    let fixedStampLogo = StampLogo;
    if (StampLogo && !StampLogo.startsWith('data:image')) {
        fixedStampLogo = `data:image/png;base64,${StampLogo}`;
    }
   

    // ตรวจสอบว่า pdfMake โหลดแล้วหรือยัง
    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    // กำหนด font
    initializePdfMake();

    const totalAmount = receipt.total_amount || 0;
    const paidAmount = receipt.paid_amount || 0;
    const remainingAmount = totalAmount - paidAmount;
    const resolvedSignatureLayout = signatureLayout
        ? normalizeSignatureLayout(signatureLayout, 'receipt')
        : getCompanySignatureLayout(company, 'receipt');
    const resolvedPrintOptions = {
        collector_name: String(
            printOptions.collector_name || company.signature_name || receipt.created_by_name || '-'
        ).trim(),
        salesperson_name: String(
            printOptions.salesperson_name || receipt.salesperson_name || receipt.sale_name || receipt.created_by_name || '-'
        ).trim(),
        show_signature: showSignature,
        show_stamp: showStamp,
        delivery_invoice_font_scale: printOptions.delivery_invoice_font_scale,
        show_document_date: printOptions.show_document_date ?? printOptions.delivery_invoice_show_date,
        deposit_payment_id: printOptions.deposit_payment_id
    };

    const depositPayments = (Array.isArray(receipt.payments) ? receipt.payments : [])
        .filter(payment => payment.payment_type === 'deposit');
    const depositPayment = depositPayments.find(
        payment => String(payment.id) === String(resolvedPrintOptions.deposit_payment_id)
    ) || depositPayments[depositPayments.length - 1] || null;
    if (printType === 'deposit_receipt' && !depositPayment) {
        throw new Error('ไม่พบรายการรับเงินมัดจำสำหรับสร้างเอกสาร');
    }

    // เลือกประเภทเอกสารตามสถานะ
    const documentType = printType === 'deposit_receipt'
        ? 'ใบเสร็จรับเงินมัดจำ (DEPOSIT RECEIPT)'
        : receipt.status === 'paid' ? 'ใบเสร็จรับเงิน (RECEIPT)' : 'ใบแจ้งหนี้ (INVOICE)';
    const documentNumber = printType === 'deposit_receipt'
        ? depositPayment?.deposit_receipt_number
        : receipt.status === 'paid' ? receipt.receipt_number : receipt.invoice_number;

    const sharedDefinitionProps = {
            printType,
            documentType,
            documentNumber,
            company,
            receipt,
            depositPayment,
            logoBase64,
            fixedSignatureLogo,
            fixedStampLogo,
            signatureLayout: resolvedSignatureLayout,
            printOptions: resolvedPrintOptions,
            totalAmount,
            paidAmount,
            remainingAmount,
            numberToThaiText,
            formatCurrency,
            dayjs
    };
    const docDefinition = printType === 'deposit_receipt'
        ? createDepositReceiptDefinition(sharedDefinitionProps)
        : printType === 'delivery_invoice'
        ? createDeliveryInvoiceDefinition(sharedDefinitionProps)
        : printType === 'a4_reference'
        ? createDocDefinitionA4Reference(sharedDefinitionProps)
        : printType === 'dot'
            ? createDocDefinitionDotStyle(sharedDefinitionProps)
            : createDocDefinitionFullStyle(sharedDefinitionProps);

    try {

        // // สร้าง PDF
        // const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);

        // // ดาวน์โหลด PDF
        // const fileName = `${receipt.status === 'paid' ? 'Receipt' : 'Invoice'}_${documentNumber || 'No'}_${dayjs().format('DDMMYYYY')}.pdf`;
        // pdfDocGenerator.download(fileName);

        //return { success: true, fileName };

        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);
        const fileName = printType === 'deposit_receipt'
            ? `DEPOSIT-RECEIPT_${String(depositPayment.deposit_receipt_number || receipt.invoice_number || 'receipt').replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')}.pdf`
            : printType === 'delivery_invoice'
            ? `DELIVERY-INVOICE_${buildReceiptPdfFileName({ ...receipt, status: 'invoice' })}`
            : buildReceiptPdfFileName(receipt);

        return new Promise((resolve, reject) => {
            pdfDocGenerator.getDataUrl((dataUrl) => {
                if (dataUrl) resolve({ dataUrl, fileName });
                else reject(new Error('ไม่สามารถสร้าง PDF preview ได้'));
            });
        });


    } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('ไม่สามารถสร้าง PDF ได้: ' + error.message);
    }
};

// ฟังก์ชันสำหรับดู PDF ในหน้าต่างใหม่
export const previewReceiptPDF = async (receipt, company = {}) => {
    if (!window.pdfMake) {
        throw new Error('pdfMake library is not loaded');
    }

    initializePdfMake();

    const totalAmount = receipt.total_amount || 0;
    const paidAmount = receipt.paid_amount || 0;
    const remainingAmount = totalAmount - paidAmount;

    // เลือกประเภทเอกสารตามสถานะ
    const documentType = receipt.status === 'paid' ? 'ใบเสร็จรับเงิน (RECEIPT)' : 'ใบแจ้งหนี้ (INVOICE)';
    const documentNumber = receipt.status === 'paid' ? receipt.receipt_number : receipt.invoice_number;

    const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 60, 40, 60],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 10
        },
        styles: {
            header: {
                fontSize: 16,
                bold: true,
                alignment: 'center',
                margin: [0, 0, 0, 20]
            },
            subheader: {
                fontSize: 12,
                bold: true,
                margin: [0, 10, 0, 5]
            },
            tableHeader: {
                bold: true,
                fontSize: 10,
                color: 'black',
                fillColor: '#eeeeee'
            },
            total: {
                fontSize: 12,
                bold: true,
                alignment: 'right'
            },
            paid: {
                fontSize: 11,
                bold: true,
                color: '#52c41a'
            },
            overdue: {
                fontSize: 11,
                bold: true,
                color: '#cf1322'
            }
        },
        content: [
            // หัวข้อ
            {
                text: documentType,
                style: 'header'
            },

            // ข้อมูลบริษัท
            {
                columns: [
                    {
                        width: '50%',
                        text: [
                            { text: 'บริษัท: ', bold: true },
                            company.name || 'ชื่อบริษัท',
                            '\n',
                            { text: 'ที่อยู่: ', bold: true },
                            company.address || 'ที่อยู่บริษัท',
                            '\n',
                            { text: 'โทรศัพท์: ', bold: true },
                            company.phone || 'หมายเลขโทรศัพท์',
                            '\n',
                            { text: 'เลขประจำตัวผู้เสียภาษี: ', bold: true },
                            company.tax_id || 'เลขประจำตัวผู้เสียภาษี'
                        ]
                    },
                    {
                        width: '50%',
                        text: [
                            { text: 'เลขที่เอกสาร: ', bold: true },
                            documentNumber || '-',
                            '\n',
                            { text: 'วันที่: ', bold: true },
                            dayjs(receipt.receipt_date).format('DD/MM/YYYY'),
                            '\n',
                            receipt.due_date ? [
                                { text: 'กำหนดชำระ: ', bold: true },
                                dayjs(receipt.due_date).format('DD/MM/YYYY'),
                                '\n'
                            ] : '',
                            { text: 'สถานะ: ', bold: true },
                            {
                                text: getReceiptStatusText(receipt.status),
                                style: receipt.status === 'paid' ? 'paid' : (receipt.status === 'overdue' ? 'overdue' : {})
                            }
                        ],
                        alignment: 'right'
                    }
                ],
                margin: [0, 0, 0, 20]
            },

            // ข้อมูลลูกค้า
            {
                text: 'ข้อมูลลูกค้า',
                style: 'subheader'
            },
            {
                text: [
                    { text: 'ชื่อลูกค้า: ', bold: true },
                    receipt.contact_name || '-',
                    '\n',
                    { text: 'บริษัท: ', bold: true },
                    getContactDocumentName(receipt),
                    '\n',
                    { text: 'ที่อยู่: ', bold: true },
                    receipt.address || '-',
                    '\n',
                    { text: 'โทรศัพท์: ', bold: true },
                    receipt.phone || '-',
                    '\n',
                    { text: 'อีเมล: ', bold: true },
                    receipt.email || '-'
                ],
                margin: [0, 0, 0, 20]
            },

            // รายการสินค้า/บริการ
            {
                text: 'รายการสินค้า/บริการ',
                style: 'subheader'
            },
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 'auto', 'auto', 'auto'],
                    body: [
                        [
                            { text: 'รายการ', style: 'tableHeader' },
                            { text: 'จำนวน', style: 'tableHeader', alignment: 'center' },
                            { text: 'ราคาต่อหน่วย', style: 'tableHeader', alignment: 'right' },
                            { text: 'รวม', style: 'tableHeader', alignment: 'right' }
                        ],
                        ...(receipt.items || []).map(item => [
                            { text: buildReceiptItemDescription(item) },
                            { text: buildReceiptItemQuantity(item), alignment: 'center' },
                            { text: item.is_free ? 'แถมฟรี' : `${(item.unit_price || 0).toLocaleString()} บาท`, alignment: 'right' },
                            { text: item.is_free ? '0.00 บาท' : `${(item.total_price || 0).toLocaleString()} บาท`, alignment: 'right' }
                        ])
                    ]
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        return (rowIndex === 0) ? '#eeeeee' : null;
                    }
                },
                margin: [0, 0, 0, 20]
            },

            // สรุปยอดเงิน
            {
                columns: [
                    { width: '60%', text: '' },
                    {
                        width: '40%',
                        table: {
                            body: [
                                [
                                    { text: 'ยอดรวมก่อนภาษี:', alignment: 'right' },
                                    { text: `${(receipt.subtotal || 0).toLocaleString()} บาท`, alignment: 'right' }
                                ],
                                [
                                    { text: 'ส่วนลด:', alignment: 'right' },
                                    { text: `${(receipt.discount || 0).toLocaleString()} บาท`, alignment: 'right' }
                                ],
                                [
                                    { text: `ภาษี (${receipt.tax_rate || 0}%):`, alignment: 'right' },
                                    { text: `${(receipt.tax_amount || 0).toLocaleString()} บาท`, alignment: 'right' }
                                ],
                                [
                                    { text: 'ยอดรวมทั้งสิ้น:', bold: true, alignment: 'right' },
                                    { text: `${totalAmount.toLocaleString()} บาท`, bold: true, alignment: 'right' }
                                ],
                                ...(paidAmount > 0 ? [
                                    [
                                        { text: 'ชำระแล้ว:', alignment: 'right', color: '#52c41a' },
                                        { text: `${paidAmount.toLocaleString()} บาท`, alignment: 'right', color: '#52c41a' }
                                    ],
                                    [
                                        { text: 'คงเหลือ:', bold: true, alignment: 'right', color: remainingAmount > 0 ? '#cf1322' : '#52c41a' },
                                        { text: `${remainingAmount.toLocaleString()} บาท`, bold: true, alignment: 'right', color: remainingAmount > 0 ? '#cf1322' : '#52c41a' }
                                    ]
                                ] : [])
                            ]
                        },
                        layout: 'noBorders'
                    }
                ]
            },

            // วิธีการชำระเงิน
            ...(receipt.payment_method ? [{
                text: 'วิธีการชำระเงิน',
                style: 'subheader',
                margin: [0, 20, 0, 5]
            }, {
                text: receipt.payment_method,
                margin: [0, 0, 0, 10]
            }] : []),

            // หมายเหตุ
            ...(receipt.notes ? [{
                text: 'หมายเหตุ',
                style: 'subheader',
                margin: [0, 20, 0, 5]
            }, {
                text: receipt.notes,
                margin: [0, 0, 0, 20]
            }] : []),

            // เงื่อนไขการชำระเงิน (สำหรับใบแจ้งหนี้)
            ...(receipt.status !== 'paid' ? [{
                text: 'เงื่อนไขการชำระเงิน',
                style: 'subheader',
                margin: [0, 20, 0, 5]
            }, {
                text: 'กรุณาชำระเงินตามกำหนดเวลาที่ระบุไว้ หากมีข้อสงสัยกรุณาติดต่อกลับ',
                margin: [0, 0, 0, 30]
            }] : []),

            // ลายเซ็น
            {
                columns: [
                    {
                        width: '50%',
                        text: [
                            'ลงชื่อ ......................................',
                            '\n\n',
                            `( ${company.signature_name} )`,
                            '\n',
                            receipt.status === 'paid' ? 'ผู้รับเงิน' : 'ผู้ออกเอกสาร'
                        ],
                        alignment: 'center'
                    },
                    {
                        width: '50%',
                        text: [
                            receipt.status === 'paid' ? 'ผู้จ่ายเงิน' : 'ผู้รับเอกสาร',
                            '\n\n',
                            'ลงชื่อ ......................................',
                            '(                                    )',
                            '\n',

                        ],
                        alignment: 'center'
                    }
                ]
            }
        ]
    };

    try {

        const pdfDocGenerator = window.pdfMake.createPdf(docDefinition);
        pdfDocGenerator.open();
        return { success: true };
    } catch (error) {
        console.error('Error previewing PDF:', error);
        throw new Error('ไม่สามารถแสดงตัวอย่าง PDF ได้: ' + error.message);
    }
};

// Helper function to get status text in Thai
const getReceiptStatusText = (status) => {
    const statusMap = {
        'invoice': 'ใบแจ้งหนี้',
        'paid': 'ชำระแล้ว',
        'partial': 'ชำระบางส่วน',
        'overdue': 'เกินกำหนด',
        'cancelled': 'ยกเลิก'
    };
    return statusMap[status] || status;
};
